import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash } from 'react-icons/hi';
import { Button, SearchBar } from '@/components/common';
import { FormattedNumberInput } from '@/utils/number-format';
import { QUANTITY_DECIMAL_PLACES } from '@/constants/number';
import { CuttingConfigLine, CuttingSize } from '@/types/cutting';
import { cuttingSizeApi } from '@/api/endpoints/cuttingSizes';

interface CuttingConfigLinesTabProps {
  configLines: CuttingConfigLine[];
  onConfigLinesChange: (configLines: CuttingConfigLine[]) => void;
  cuttingConfigId?: string;
}

export const CuttingConfigLinesTab: React.FC<CuttingConfigLinesTabProps> = ({
  configLines,
  onConfigLinesChange,
  cuttingConfigId,
}) => {
  const { t } = useTranslation();

  // State for cutting size search and selection
  const [cuttingSizeSearchQuery, setCuttingSizeSearchQuery] = useState('');
  const [cuttingSizeSearchResults, setCuttingSizeSearchResults] = useState<CuttingSize[]>([]);
  const [showCuttingSizeDropdown, setShowCuttingSizeDropdown] = useState(false);
  const [loadingCuttingSizes, setLoadingCuttingSizes] = useState(false);

  // State for storing cutting size details for display (fallback for lines without embedded cutting size)
  const [cuttingSizeDetails, setCuttingSizeDetails] = useState<Map<string, CuttingSize>>(new Map());
  const [loadingCuttingSizeDetails, setLoadingCuttingSizeDetails] = useState(false);

  // Memoize the unique cutting size IDs that need to be loaded (only those without embedded cutting size data)
  const uniqueCuttingSizeIds = useMemo(() => {
    return [...new Set(configLines
      .filter(line => line.cuttingSizeId && line.cuttingSizeId.trim() !== '' && !line.cuttingSize)
      .map(line => line.cuttingSizeId)
    )];
  }, [configLines]);

  // Create a stable string representation of the IDs for dependency tracking
  const cuttingSizeIdsKey = useMemo(() => {
    return uniqueCuttingSizeIds.sort().join(',');
  }, [uniqueCuttingSizeIds]);

  // Load cutting size details for config lines that don't have embedded cutting size data
  useEffect(() => {
    if (uniqueCuttingSizeIds.length === 0) {
      setCuttingSizeDetails(new Map());
      setLoadingCuttingSizeDetails(false);
      return;
    }

    let isMounted = true; // Flag to prevent state updates if component unmounts

    const loadCuttingSizeDetails = async () => {
      // Check which IDs we need to load
      const currentDetails = cuttingSizeDetails;
      const missingIds = uniqueCuttingSizeIds.filter(id => !currentDetails.has(id));

      if (missingIds.length === 0) {
        return; // All details already loaded
      }

      setLoadingCuttingSizeDetails(true);

      try {
        const detailsMap = new Map(currentDetails); // Start with existing details

        // Load only missing cutting size details
        const loadPromises = missingIds.map(async (cuttingSizeId) => {
          try {
            const cuttingSize = await cuttingSizeApi.getCuttingSize(cuttingSizeId);
            if (cuttingSize && isMounted) {
              detailsMap.set(cuttingSizeId, cuttingSize);
            }
          } catch (error) {
            console.error(`Error loading cutting size ${cuttingSizeId}:`, error);
            // Don't add to map if failed to load
          }
        });

        await Promise.all(loadPromises);

        if (isMounted) {
          setCuttingSizeDetails(detailsMap);
        }
      } catch (error) {
        console.error('Error loading cutting size details:', error);
        // Keep existing details on error
      } finally {
        if (isMounted) {
          setLoadingCuttingSizeDetails(false);
        }
      }
    };

    loadCuttingSizeDetails();

    return () => {
      isMounted = false; // Cleanup flag
    };
  }, [cuttingSizeIdsKey]); // Only depend on the stable string key

  // Search cutting sizes
  useEffect(() => {
    const searchCuttingSizes = async () => {
      if (cuttingSizeSearchQuery.length < 1) {
        setCuttingSizeSearchResults([]);
        return;
      }

      setLoadingCuttingSizes(true);
      try {
        const response = await cuttingSizeApi.getCuttingSizes(1, 20, {
          search: cuttingSizeSearchQuery,
        });
        
        // DEBUG: Log search results
        console.log('🔍 Search query:', cuttingSizeSearchQuery);
        console.log('🔍 Search response:', response);
        console.log('🔍 Search results data:', response.data);
        response.data.forEach((item, index) => {
          console.log(`🔍 Search result ${index}:`, item);
          console.log(`🔍 Search result ${index} - code:`, item.code);
          console.log(`🔍 Search result ${index} - size:`, item.size);
        });
        
        setCuttingSizeSearchResults(response.data);
      } catch (error) {
        console.error('Error searching cutting sizes:', error);
        setCuttingSizeSearchResults([]);
      } finally {
        setLoadingCuttingSizes(false);
      }
    };

    const debounceTimer = setTimeout(searchCuttingSizes, 200);
    return () => clearTimeout(debounceTimer);
  }, [cuttingSizeSearchQuery]);

  const selectCuttingSize = (cuttingSize: CuttingSize) => {
    // DEBUG: Log selected cutting size
    console.log('🔍 Selected cutting size:', cuttingSize);
    console.log('🔍 Cutting size code:', cuttingSize.code);
    console.log('🔍 Cutting size size:', cuttingSize.size);
    
    // Validate cutting size
    if (!cuttingSize || !cuttingSize.code) {
      console.error('Invalid cutting size selected');
      return;
    }

    // Check if cutting size is already added
    const existingLine = configLines.find(line => line.cuttingSizeId === cuttingSize.code);
    if (existingLine) {
      // Focus on existing line or show message
      console.log(`Cutting size ${cuttingSize.code} is already added`);
      return;
    }

    // Add new config line with selected cutting size
    const newConfigLine: CuttingConfigLine = {
      id: `temp-${Date.now()}`,
      code: '',
      cuttingConfigId: cuttingConfigId || '',
      cuttingSizeId: cuttingSize.code,
      cuttingSize: cuttingSize, // Embed the full cutting size data
      quantity: 1,
      lossRate: 0,
      note: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    console.log('🔍 Created config line:', newConfigLine);
    onConfigLinesChange([...configLines, newConfigLine]);

    // Clear search
    setCuttingSizeSearchQuery('');
    setCuttingSizeSearchResults([]);
    setShowCuttingSizeDropdown(false);
  };

  // Config line management functions
  const addConfigLine = () => {
    const newConfigLine: CuttingConfigLine = {
      id: `temp-${Date.now()}`,
      code: '',
      cuttingConfigId: cuttingConfigId || '',
      cuttingSizeId: '',
      quantity: 1,
      lossRate: 0,
      note: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onConfigLinesChange([...configLines, newConfigLine]);
  };

  const removeConfigLine = (index: number) => {
    // Validate index
    if (index < 0 || index >= configLines.length) {
      console.error('Invalid config line index for removal:', index);
      return;
    }

    const updatedLines = configLines.filter((_, i) => i !== index);
    onConfigLinesChange(updatedLines);
  };

  const updateConfigLine = (index: number, field: keyof CuttingConfigLine, value: string | number) => {
    // Validate index
    if (index < 0 || index >= configLines.length) {
      console.error('Invalid config line index:', index);
      return;
    }

    // Validate and sanitize values based on field type
    let sanitizedValue = value;
    if (field === 'quantity') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      sanitizedValue = Math.max(1, numValue || 1); // Ensure minimum quantity of 1
    } else if (field === 'lossRate') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      sanitizedValue = Math.max(0, Math.min(1, numValue || 0)); // Ensure between 0 and 1
    }

    const updatedLines = configLines.map((line, i) => {
      if (i === index) {
        return {
          ...line,
          [field]: sanitizedValue,
          updatedAt: new Date().toISOString() // Update timestamp
        };
      }
      return line;
    });
    onConfigLinesChange(updatedLines);
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t('cuttingConfigs.configLines.title')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('cuttingConfigs.configLines.description')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addConfigLine}
        >
          <HiPlus className="w-4 h-4 mr-2" />
          {t('cuttingConfigs.configLines.add')}
        </Button>
      </div>

      {/* Cutting Size Search */}
      <div className="relative">
        <div className="relative">
          <SearchBar
            value={cuttingSizeSearchQuery}
            onChange={(value) => {
              setCuttingSizeSearchQuery(value);
              setShowCuttingSizeDropdown(value.length >= 1);
            }}
            placeholder={t('cuttingConfigs.configLines.searchCuttingSize')}
            debounceMs={100}
          />
        </div>

        {/* Search Results Dropdown */}
        {showCuttingSizeDropdown && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {loadingCuttingSizes ? (
              <div className="px-4 py-2 text-sm text-gray-500">
                {t('common.loading')}...
              </div>
            ) : cuttingSizeSearchResults.length > 0 ? (
              cuttingSizeSearchResults.map((cuttingSize) => (
                <button
                  key={cuttingSize.id}
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                  onClick={() => selectCuttingSize(cuttingSize)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{cuttingSize.size}</span>
                    <span className="text-xs text-gray-500">{cuttingSize.code}</span>
                  </div>
                  {cuttingSize.note && (
                    <div className="text-xs text-gray-500 mt-1">{cuttingSize.note}</div>
                  )}
                </button>
              ))
            ) : cuttingSizeSearchQuery.length >= 1 ? (
              <div className="px-4 py-2 text-sm text-gray-500">
                {t('cuttingConfigs.configLines.noCuttingSizesFound')}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Config Lines Table */}
      {configLines.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.code')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cuttingConfigs.configLines.fields.cuttingSizeCode')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cuttingConfigs.configLines.fields.cuttingSize')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cuttingConfigs.configLines.fields.quantity')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cuttingConfigs.configLines.fields.lossRate')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cuttingConfigs.configLines.fields.note')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {configLines.map((line, index) => (
                  <tr key={line.id}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {line.code || (
                          <span className="text-gray-400 italic">
                            {line.id.startsWith('temp-') ? t('common.new') : t('common.noCode')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {line.cuttingSizeId ? (
                          (() => {
                            // Use embedded cutting size data if available, otherwise fall back to loaded details
                            const cuttingSize = line.cuttingSize || cuttingSizeDetails.get(line.cuttingSizeId);
                            const isLoading = !line.cuttingSize && loadingCuttingSizeDetails && !cuttingSizeDetails.has(line.cuttingSizeId);

                            if (isLoading) {
                              return (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600 mr-2"></div>
                                  <span className="text-gray-500">{t('common.loading')}...</span>
                                </div>
                              );
                            }

                            return (
                              <div className="text-sm font-medium text-gray-900">
                                {cuttingSize?.code || line.cuttingSizeId}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-gray-500 italic">
                            {t('common.notSelected')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {line.cuttingSizeId ? (
                          (() => {
                            // Use embedded cutting size data if available, otherwise fall back to loaded details
                            const cuttingSize = line.cuttingSize || cuttingSizeDetails.get(line.cuttingSizeId);
                            const isLoading = !line.cuttingSize && loadingCuttingSizeDetails && !cuttingSizeDetails.has(line.cuttingSizeId);

                            // DEBUG: Log cutting size data for this line
                            console.log(`🔍 Line ${line.id} - cuttingSizeId:`, line.cuttingSizeId);
                            console.log(`🔍 Line ${line.id} - embedded cuttingSize:`, line.cuttingSize);
                            console.log(`🔍 Line ${line.id} - loaded cuttingSize:`, cuttingSizeDetails.get(line.cuttingSizeId));
                            console.log(`🔍 Line ${line.id} - final cuttingSize:`, cuttingSize);
                            console.log(`🔍 Line ${line.id} - size value:`, cuttingSize?.size);

                            if (isLoading) {
                              return (
                                <div className="flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600 mr-2"></div>
                                  <span className="text-gray-500">{t('common.loading')}...</span>
                                </div>
                              );
                            }

                            return (
                              <div>
                                <div className="font-medium">
                                  {cuttingSize?.size || t('cuttingConfigs.configLines.noSizeInfo')}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-gray-500 italic">
                            {t('cuttingConfigs.configLines.selectCuttingSize')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <FormattedNumberInput
                        value={line.quantity}
                        onChange={(value) => updateConfigLine(index, 'quantity', Math.max(1, value))}
                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                        maxDecimalPlaces={QUANTITY_DECIMAL_PLACES}
                        title={t('cuttingConfigs.configLines.fields.quantity')}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={line.lossRate.toString()}
                        onChange={(e) => updateConfigLine(index, 'lossRate', e.target.value)}
                        min="0"
                        max="1"
                        step="0.01"
                        className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                        title={t('cuttingConfigs.configLines.fields.lossRate')}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={line.note}
                        onChange={(e) => updateConfigLine(index, 'note', e.target.value)}
                        placeholder={t('cuttingConfigs.configLines.notePlaceholder')}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                        title={t('cuttingConfigs.configLines.fields.note')}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeConfigLine(index)}
                        className="text-red-600 hover:text-red-900 border-none hover:bg-red-50"
                        title={t('common.delete')}
                        aria-label={`${t('common.delete')} ${t('cuttingConfigs.configLines.fields.cuttingSize')}`}
                      >
                        <HiTrash className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>{t('cuttingConfigs.configLines.noLines')}</p>
          <p className="text-sm mt-1">{t('cuttingConfigs.configLines.addFirstLine')}</p>
        </div>
      )}
    </div>
  );
};
